import type {
  CanvasFileItem,
  CardPosition,
  QuoteCanvasData,
} from './types'

export const emptyCanvasData = (): QuoteCanvasData => ({
  version: 1,
  positions: {},
  zOrder: [],
  comments: [],
  files: [],
  links: [],
  strokes: [],
  stamps: [],
})

export const normalizePositions = (
  raw?: Record<string | number, CardPosition> | null
): Record<number, CardPosition> => {
  if (!raw) return {}
  const next: Record<number, CardPosition> = {}
  for (const [key, value] of Object.entries(raw)) {
    const id = Number(key)
    if (
      !Number.isFinite(id) ||
      !value ||
      typeof value.x !== 'number' ||
      typeof value.y !== 'number'
    ) {
      continue
    }
    next[id] = { x: value.x, y: value.y }
  }
  return next
}

const asArray = <T>(value: unknown): T[] => (Array.isArray(value) ? value : [])

export const parseCanvasData = (raw: unknown): QuoteCanvasData => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return emptyCanvasData()
  }
  const data = raw as Partial<QuoteCanvasData> & {
    files?: Array<CanvasFileItem & { dataUrl?: string }>
  }
  return {
    version: 1,
    positions: normalizePositions(data.positions),
    zOrder: asArray<number>(data.zOrder)
      .map(Number)
      .filter((id) => Number.isFinite(id)),
    comments: asArray(data.comments),
    files: asArray(data.files).map((file) => {
      const item = file as CanvasFileItem & { dataUrl?: string }
      const { dataUrl, ...rest } = item
      return { ...rest, url: rest.url ?? dataUrl }
    }),
    links: asArray(data.links),
    strokes: asArray(data.strokes),
    stamps: asArray(data.stamps),
  }
}

export const isCanvasDataEmpty = (data: QuoteCanvasData) =>
  Object.keys(data.positions).length === 0 &&
  data.zOrder.length === 0 &&
  data.comments.length === 0 &&
  data.files.length === 0 &&
  data.links.length === 0 &&
  data.strokes.length === 0 &&
  data.stamps.length === 0

/** Never persist data URLs on the quote row. */
export const toPersistedCanvasData = (data: QuoteCanvasData): QuoteCanvasData => ({
  ...data,
  files: data.files.map((file) => ({
    ...file,
    url: file.url && !file.url.startsWith('data:') ? file.url : undefined,
  })),
})
